import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { HawkErrorBoundary } from "@/lib/error-boundary";
import { initHawk, reportError } from "@/lib/hawk";
import { localeReady } from "@/lib/i18n";
import { redirectToOnboarding, redirectToSignIn } from "@/lib/session";
import { probeSession } from "@/lib/session-boot";
import { AppProviders } from "@/providers/app-providers";
import { routeTree } from "./routeTree.gen";
import "./styles.css";

void initHawk("id-web");

const router = createRouter({
	routeTree,
	defaultPreload: "intent",
	defaultPreloadStaleTime: 0,
	scrollRestoration: true,
	defaultOnCatch: (error, errorInfo) => {
		reportError(error instanceof Error ? error : String(error), {
			source: "router",
			componentStack: errorInfo.componentStack,
		});
	},
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

/** Сколько ждать словарь, прежде чем рисовать на английском. */
const LOCALE_WAIT_MS = 1500;

/**
 * Первый рендер — уже на нужном языке И уже за сессией.
 *
 * ПОРЯДОК ЗДЕСЬ — ЭТО ТО, ЧТО ВИДИТ ЧЕЛОВЕК. Витрина целиком живёт за cookie:
 * анонимный заход всегда заканчивается уходом на `auth.`. Пока это решение
 * принималось внутри React (`SessionGate`), уход был четвёртым кадром из
 * четырёх — сначала пустая канва, потом баннер cookie посреди неё, потом
 * спиннер, и только затем другой домен. Три кадра рисовались человеку,
 * который витрину не увидит.
 *
 * Поэтому `probeSession` стоит ДО `createRoot`: анонимный заход уходит с
 * ровной канвы, ничего не отрисовав, а вошедший получает первый кадр уже с
 * профилем в кэше — без спиннера гейта поверх уже полученного ответа.
 *
 * Словарь локали приезжает отдельным чанком (21 локаль, в основном бандле
 * только английский), поэтому без ожидания человек видел английский интерфейс,
 * который через мгновение переписывался: менялся текст, а с ним и высота
 * блоков. Чанк грузится параллельно с бандлом, так что ожидание почти всегда
 * нулевое, а `Promise.race` страхует от медленной сети — лучше английский
 * интерфейс, чем экран, который не появился.
 */
async function mount(): Promise<void> {
	// Вопрос о сессии задаётся ПЕРВЫМ и параллельно словарю: и то, и другое —
	// сеть, ждать их по очереди незачем. Анонимного человека мы к словарю даже
	// не подпускаем — он уйдёт на `auth.` раньше, чем словарь понадобится.
	const session = await probeSession();

	if (session.state === "anonymous") {
		redirectToSignIn();
		return;
	}
	if (session.state === "session" && session.me.needsOnboarding) {
		redirectToOnboarding();
		return;
	}

	await Promise.race([localeReady, new Promise((resolve) => setTimeout(resolve, LOCALE_WAIT_MS))]);

	// Элемент ищем здесь, а не на верхнем уровне: скелет из `index.html` лежит
	// внутри него, и React очистит контейнер ровно в момент первого рендера.
	const rootElement = document.getElementById("root");
	if (!rootElement) throw new Error("Не найден #root — проверь index.html");

	createRoot(rootElement).render(
		<StrictMode>
			<HawkErrorBoundary>
				<AppProviders initialMe={session.state === "session" ? session.me : undefined}>
					<RouterProvider router={router} />
				</AppProviders>
			</HawkErrorBoundary>
		</StrictMode>,
	);
}

void mount();
